import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useMicrocuencasVariablesCRUD } from '../hooks/useMicrocuencasVariablesCRUD'

export default function TablaCompleta() {
  const { microcuencas, loading } = useMicrocuencasVariablesCRUD()
  const [sortColumn, setSortColumn] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const sortedData = [...microcuencas].sort((a, b) => {
    if (!sortColumn) return 0
    
    const aVal = a[sortColumn]
    const bVal = b[sortColumn]
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
    }
    
    const aStr = String(aVal || '')
    const bStr = String(bVal || '')
    return sortDirection === 'asc' 
      ? aStr.localeCompare(bStr) 
      : bStr.localeCompare(aStr)
  })

  const getCategoryColor = (categoria: string) => {
    switch (categoria) {
      case 'Muy Alta': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'Alta': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'Media': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'Baja': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'Muy Baja': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Cargando datos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-8 px-4">
      <div className="max-w-[98%] mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Tabla Completa de Microcuencas
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Visualización completa de todas las variables ({microcuencas.length} microcuencas)
              </p>
            </div>
            <Link
              to="/"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              ← Inicio
            </Link>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('codigo')}>
                    Código {sortColumn === 'codigo' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('municipio')}>
                    Municipio {sortColumn === 'municipio' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('microcuenca')}>
                    Microcuenca {sortColumn === 'microcuenca' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('region')}>
                    Región {sortColumn === 'region' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('priorizacion_ranking')}>
                    Ranking {sortColumn === 'priorizacion_ranking' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('priorizacion_valor')}>
                    Priorización {sortColumn === 'priorizacion_valor' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('categoria')}>
                    Categoría {sortColumn === 'categoria' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('densidad_poblacion')}>
                    Densidad Pob. {sortColumn === 'densidad_poblacion' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('rezago_social')}>
                    Rezago Social {sortColumn === 'rezago_social' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('superficie_microcuenca')}>
                    Sup. Microcuenca {sortColumn === 'superficie_microcuenca' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('superficie_forestal')}>
                    Sup. Forestal {sortColumn === 'superficie_forestal' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('superficie_pastizal')}>
                    Sup. Pastizal {sortColumn === 'superficie_pastizal' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('superficie_agricola')}>
                    Sup. Agrícola {sortColumn === 'superficie_agricola' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('superficie_asentamiento')}>
                    Sup. Asentamiento {sortColumn === 'superficie_asentamiento' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('superficie_cafe')}>
                    Sup. Café {sortColumn === 'superficie_cafe' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('superficie_incendios')}>
                    Sup. Incendios {sortColumn === 'superficie_incendios' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('tiempo_concentracion')}>
                    T. Concentración {sortColumn === 'tiempo_concentracion' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('pendiente')}>
                    Pendiente {sortColumn === 'pendiente' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('indice_gravelius')}>
                    Índice Gravelius {sortColumn === 'indice_gravelius' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('etp')}>
                    ETP {sortColumn === 'etp' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('deforestacion')}>
                    Deforestación {sortColumn === 'deforestacion' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('erosion')}>
                    Erosión {sortColumn === 'erosion' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('escurrimiento')}>
                    Escurrimiento {sortColumn === 'escurrimiento' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700" onClick={() => handleSort('precipitacion')}>
                    Precipitación {sortColumn === 'precipitacion' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedData.map((item, index) => (
                  <tr 
                    key={item._id || index}
                    className="hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors duration-150"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">{item.codigo}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.municipio}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-semibold">{item.microcuenca}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{item.region}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-bold rounded-full">
                        {item.priorizacion_ranking}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 font-semibold">
                      {item.priorizacion_valor?.toFixed(3)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(item.categoria)}`}>
                        {item.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.densidad_poblacion?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.rezago_social?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.superficie_microcuenca?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.superficie_forestal?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.superficie_pastizal?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.superficie_agricola?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.superficie_asentamiento?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.superficie_cafe?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.superficie_incendios?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.tiempo_concentracion?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.pendiente?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.indice_gravelius?.toFixed(3)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.etp?.toFixed(1)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.deforestacion?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.erosion?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.escurrimiento?.toFixed(0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300">{item.precipitacion?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
